import { useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ReportCategory, ReportSeverity, ReportStatus, formatRobotShortId } from '@bhops/shared';
import { reportsApi, type ListReportsQuery } from '@/api/reports';
import { extractApiError } from '@/api/client';
import { kstDateTime } from '@/lib/format-date';
import { Button, Field, Textarea } from '@/components/Form';
import { ConfirmActionModal } from '@/components/ConfirmActionModal';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import { canEditField } from '@/permissions';
import { ServiceOrderCreateModal } from './ServiceOrderCreateModal';

const STATUS_LABEL: Record<ReportStatus, string> = {
  NEW: '신규',
  TRIAGED: '분류완료',
  ASSIGNED: '배정',
  IN_PROGRESS: '진행중',
  RESOLVED: '해결',
  DISMISSED: '반려',
  DUPLICATE: '중복',
};

const CATEGORY_LABEL: Record<ReportCategory, string> = {
  HARDWARE_FAILURE: '하드웨어 고장',
  COOKING_QUALITY: '조리 품질',
  CLEANING_HYGIENE: '청소 · 위생',
  DISPENSING: '토출 · 공급',
  CONNECTIVITY: '통신',
  UI_UX: 'UI',
  OTHER: '기타',
};

const PAGE_SIZE = 50;

export function ReportsInboxPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // A3 — the dashboard "SLA 초과 리포트" hero deep-links here with ?sla=breached.
  const slaOnly = searchParams.get('sla') === 'breached';
  const setSlaOnly = (on: boolean) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (on) next.set('sla', 'breached');
      else next.delete('sla');
      return next;
    });
  };
  const user = useAuthStore((s) => s.user);
  // round3 #8c — 트리아지 권한을 백엔드 CASL(CustomerReport.update: ADMIN/FIELD_MANAGER/
  // MANAGER)과 일치시킨다. 기존 ADMIN||MANAGER 는 FIELD_MANAGER(필드팀장)를 누락해
  // 정작 담당 역할이 트리아지 버튼을 못 보던 RBAC drift.
  const canTriage = !!user && canEditField(user.role);

  const [filters, setFilters] = useState<ListReportsQuery>({
    page: 1,
    pageSize: PAGE_SIZE,
    openOnly: true,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveNote, setResolveNote] = useState('');
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  // docs/work_log/p0-service-order-create-modal — 리포트에서 SO 로 전환.
  const [soCreateOpen, setSoCreateOpen] = useState(false);

  async function handleExport(): Promise<void> {
    setExporting(true);
    try {
      const { page: _p, pageSize: _ps, ...rest } = filters;
      await reportsApi.exportCsv(rest);
    } catch (err) {
      toast.error(extractApiError(err), 'CSV 내보내기 실패');
    } finally {
      setExporting(false);
    }
  }

  const listQ = useQuery({
    queryKey: ['reports', filters],
    queryFn: () => reportsApi.list(filters),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const selectedQ = useQuery({
    queryKey: ['report', selectedId],
    queryFn: () => reportsApi.get(selectedId!),
    enabled: Boolean(selectedId),
  });

  const update = useMutation({
    mutationFn: (args: {
      id: string;
      body: Parameters<typeof reportsApi.update>[1];
      doneMessage: string;
    }) => reportsApi.update(args.id, args.body),
    onSuccess: (_, args) => {
      qc.invalidateQueries({ queryKey: ['reports'] });
      qc.invalidateQueries({ queryKey: ['report', args.id] });
      toast.success(args.doneMessage);
      setResolveOpen(false);
      setResolveNote('');
      setDismissOpen(false);
      setDismissReason('');
    },
    onError: (err) => toast.error(extractApiError(err), '업데이트 실패'),
  });

  // data-lifecycle §3/D3 (PR-5b) — 셀프 소프트삭제 / 복구 / 완전삭제.
  const [hardDeleteId, setHardDeleteId] = useState<string | null>(null);
  const softDelete = useMutation({
    mutationFn: (id: string) => reportsApi.softDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports'] });
      toast.success('리포트를 삭제했습니다. 나중에 복구할 수 있습니다.');
    },
    onError: (err) => toast.error(extractApiError(err), '삭제 실패'),
  });
  const restore = useMutation({
    mutationFn: (id: string) => reportsApi.restore(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports'] });
      toast.success('리포트를 복구했습니다.');
    },
    onError: (err) => toast.error(extractApiError(err), '복구 실패'),
  });
  const hardDelete = useMutation({
    mutationFn: (id: string) => reportsApi.hardDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports'] });
      toast.success('리포트를 완전히 삭제했습니다.');
      setHardDeleteId(null);
    },
    onError: (err) => toast.error(extractApiError(err), '완전 삭제 실패'),
  });

  // A3 — the list API has no SLA filter, so when ?sla=breached is active we narrow the
  // loaded page client-side to SLA-breached rows (best-effort; SLA-breached reports are
  // open, so they fall within the default openOnly page).
  const rawItems = listQ.data?.items ?? [];
  const items = slaOnly ? rawItems.filter((r) => r.slaBreachedAt) : rawItems;

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            리포트 인박스
          </h1>
          <select
            value={filters.status ?? ''}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                status: (e.target.value || undefined) as ReportStatus | undefined,
                page: 1,
              }))
            }
            style={select}
          >
            <option value="">전체 상태</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <select
            value={filters.severity ?? ''}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                severity: (e.target.value || undefined) as ReportSeverity | undefined,
                page: 1,
              }))
            }
            style={select}
          >
            <option value="">전체 긴급도</option>
            <option value={ReportSeverity.URGENT}>긴급만</option>
            <option value={ReportSeverity.NORMAL}>일반만</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={Boolean(filters.openOnly)}
              onChange={(e) => setFilters((f) => ({ ...f, openOnly: e.target.checked, page: 1 }))}
            />
            미해결만
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={Boolean(filters.assignedToMe)}
              onChange={(e) =>
                setFilters((f) => ({ ...f, assignedToMe: e.target.checked, page: 1 }))
              }
            />
            나에게 배정
          </label>
          {/* data-lifecycle §3 (PR-5b) — 삭제됨 포함(삭제 권한자 전용). 복구/완전삭제용.
              체크박스 순서상 마지막에 둔다(테스트가 0=미해결만·1=나에게배정 인덱스 가정). */}
          {canTriage && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={Boolean(filters.includeDeleted)}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    includeDeleted: e.target.checked || undefined,
                    page: 1,
                  }))
                }
              />
              삭제됨 포함
            </label>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={slaOnly}
              onChange={(e) => setSlaOnly(e.target.checked)}
            />
            SLA 초과만
          </label>
          <div
            className="kuun-caption"
            style={{
              marginLeft: 'auto',
              color: listQ.isFetching ? 'var(--brand)' : 'var(--fg-muted)',
            }}
          >
            {listQ.isFetching ? '불러오는 중…' : `${listQ.data?.total ?? 0} 건`}
          </div>
          <Button type="button" variant="secondary" onClick={handleExport} disabled={exporting}>
            {exporting ? '내보내는 중…' : 'CSV 내보내기'}
          </Button>
        </div>

        <div style={{ display: 'flex', gap: 12, flex: 1, minHeight: 0 }}>
          {/* List (left) */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--r-card)',
              background: 'var(--bg-surface)',
              overflow: 'auto',
            }}
          >
            {listQ.isLoading ? (
              <div style={empty}>
                <span className="kuun-caption">불러오는 중…</span>
              </div>
            ) : listQ.error ? (
              <div style={empty}>
                <span style={{ color: 'var(--fg-danger)' }}>{extractApiError(listQ.error)}</span>
              </div>
            ) : items.length === 0 ? (
              <div style={empty} className="kuun-dot-grid">
                <div className="kuun-h4">처리할 리포트가 없습니다</div>
                <div className="kuun-caption" style={{ marginTop: 4 }}>
                  고객이 QR을 찍어 리포트를 보내면 여기에 표시됩니다.
                </div>
              </div>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={th}>긴급</th>
                    <th style={th}>ID</th>
                    <th style={th}>상태</th>
                    <th style={th}>로봇</th>
                    <th style={th}>분류</th>
                    <th style={th}>미리보기</th>
                    <th style={th}>제출 시각</th>
                    {canTriage && <th style={th}></th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const sn = r.robot.serialShort
                      ? formatRobotShortId(r.robot.serialShort)
                      : (r.robot.serialLong ?? '–');
                    return (
                      <tr
                        key={r.id}
                        onClick={() => setSelectedId(r.id)}
                        style={{
                          cursor: 'pointer',
                          borderTop: '1px solid var(--border-subtle)',
                          background: selectedId === r.id ? 'var(--bg-selected)' : undefined,
                          // data-lifecycle §3 — 삭제됨 row 는 흐리게.
                          opacity: r.deletedAt ? 0.55 : 1,
                        }}
                      >
                        <td style={td}>
                          {r.severity === ReportSeverity.URGENT ? (
                            <span
                              className="kuun-urgent"
                              style={{
                                display: 'inline-block',
                                padding: '1px 8px',
                                borderRadius: 'var(--r-pill)',
                                fontSize: 11,
                                fontWeight: 600,
                                background: 'var(--bg-danger-subtle)',
                                color: 'var(--fg-danger)',
                                border: '1px solid var(--fg-danger)',
                              }}
                            >
                              긴급
                            </span>
                          ) : (
                            <span className="kuun-caption">일반</span>
                          )}
                          {r.slaBreachedAt && (
                            <span
                              title="SLA 초과"
                              style={{
                                marginLeft: 6,
                                display: 'inline-block',
                                padding: '1px 6px',
                                borderRadius: 'var(--r-pill)',
                                fontSize: 10,
                                fontWeight: 700,
                                background: 'var(--bg-danger-subtle)',
                                color: 'var(--fg-danger)',
                                border: '1px solid var(--fg-danger)',
                              }}
                            >
                              SLA 초과
                            </span>
                          )}
                        </td>
                        <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                          {r.displayId ?? '–'}
                        </td>
                        <td style={td}>
                          {r.deletedAt ? (
                            <span style={{ color: 'var(--severity-urgent)', fontWeight: 600 }}>
                              삭제됨
                            </span>
                          ) : (
                            STATUS_LABEL[r.status]
                          )}
                        </td>
                        <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                          <Link to={`/robots/${r.robot.id}`} style={{ color: 'var(--fg-brand)' }}>
                            {sn}
                          </Link>
                        </td>
                        <td style={td}>{CATEGORY_LABEL[r.category]}</td>
                        <td
                          style={{
                            ...td,
                            maxWidth: 240,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {r.description}
                        </td>
                        <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                          {/* 추가수정 2차(26-07-27) §2 — 브라우저 로캘 대신 KST 고정.
                              접수 시각은 SLA 기준이라 시각까지 남긴다. */}
                          {kstDateTime(r.submittedAt) ?? '–'}
                        </td>
                        {/* data-lifecycle §3/D3 — 삭제/복구/완전삭제(삭제 권한자). 행 클릭 전파 차단. */}
                        {canTriage && (
                          <td
                            style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {r.deletedAt ? (
                              <span style={{ display: 'inline-flex', gap: 6 }}>
                                <button
                                  type="button"
                                  onClick={() => restore.mutate(r.id)}
                                  disabled={restore.isPending}
                                  style={lifecycleBtn('var(--fg-brand)')}
                                >
                                  복구
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setHardDeleteId(r.id)}
                                  title="이 리포트를 완전히 삭제합니다. 되돌릴 수 없습니다."
                                  style={lifecycleBtn('var(--severity-urgent)')}
                                >
                                  완전 삭제
                                </button>
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => softDelete.mutate(r.id)}
                                disabled={softDelete.isPending}
                                title="삭제합니다. 나중에 복구할 수 있습니다."
                                style={lifecycleBtn('var(--fg-muted)')}
                              >
                                삭제
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Detail (right) */}
          <div
            style={{
              width: 380,
              flexShrink: 0,
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--r-card)',
              background: 'var(--bg-surface)',
              overflow: 'auto',
            }}
          >
            {!selectedId ? (
              <div style={empty}>
                <span className="kuun-caption">리포트를 선택해 주세요.</span>
              </div>
            ) : selectedQ.isLoading ? (
              <div style={empty}>
                <span className="kuun-caption">불러오는 중…</span>
              </div>
            ) : selectedQ.error ? (
              <div style={empty}>
                <span style={{ color: 'var(--fg-danger)' }}>
                  {extractApiError(selectedQ.error)}
                </span>
              </div>
            ) : (
              selectedQ.data && (
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <div className="kuun-label">현재 상태</div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {STATUS_LABEL[selectedQ.data.status]}
                    </div>
                  </div>
                  <KV k="분류" v={CATEGORY_LABEL[selectedQ.data.category]} />
                  <KV
                    k="긴급도"
                    v={selectedQ.data.severity === ReportSeverity.URGENT ? '긴급' : '일반'}
                  />
                  <KV
                    k="로봇"
                    v={
                      selectedQ.data.robot.serialShort
                        ? formatRobotShortId(selectedQ.data.robot.serialShort)
                        : (selectedQ.data.robot.serialLong ?? '–')
                    }
                  />
                  <KV
                    k="매장"
                    v={
                      selectedQ.data.robot.currentStore
                        ? [
                            selectedQ.data.robot.currentStore.name,
                            selectedQ.data.robot.currentStore.branchName,
                          ]
                            .filter(Boolean)
                            .join(' ')
                        : '–'
                    }
                  />
                  {selectedQ.data.contact && <KV k="연락처" v={selectedQ.data.contact} />}
                  <div>
                    <div className="kuun-label" style={{ marginBottom: 4 }}>
                      상세 내용
                    </div>
                    <div
                      style={{
                        padding: 10,
                        background: 'var(--bg-sunken)',
                        borderRadius: 'var(--r-card)',
                        fontSize: 13,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {selectedQ.data.description}
                    </div>
                  </div>

                  {canTriage && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        paddingTop: 8,
                        borderTop: '1px solid var(--border-subtle)',
                      }}
                    >
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                          type="button"
                          onClick={() => {
                            const assignedToId = user?.id;
                            if (!assignedToId) return;
                            update.mutate({
                              id: selectedQ.data!.id,
                              body: {
                                status: ReportStatus.ASSIGNED,
                                assignedToId,
                              },
                              doneMessage: '리포트를 나에게 배정했습니다.',
                            });
                          }}
                          disabled={update.isPending}
                        >
                          나에게 배정
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() =>
                            update.mutate({
                              id: selectedQ.data!.id,
                              body: { status: ReportStatus.IN_PROGRESS },
                              doneMessage: '리포트를 진행중으로 바꿨습니다.',
                            })
                          }
                          disabled={update.isPending}
                        >
                          진행중
                        </Button>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                          type="button"
                          onClick={() => setResolveOpen(true)}
                          disabled={update.isPending}
                        >
                          해결
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          onClick={() => setDismissOpen(true)}
                          disabled={update.isPending}
                        >
                          반려
                        </Button>
                      </div>
                      {/* round5 #14 — 모든 리포트가 REACTIVE SO 를 자동
                          생성하므로 '→ SO 전환'(항상 2번째 중복 SO 를
                          만들던 버튼)을 '연결된 SO 열기' 로 교체. 연결 SO
                          가 없는 구버전 리포트만 기존 전환 버튼 유지. */}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {(selectedQ.data.serviceOrders?.length ?? 0) > 0 ? (
                          selectedQ.data.serviceOrders!.map((so) => (
                            <Button
                              key={so.id}
                              type="button"
                              variant="secondary"
                              onClick={() =>
                                navigate('/service-orders', { state: { openId: so.id } })
                              }
                              data-testid="report-open-linked-so"
                            >
                              연결된 서비스 오더 {so.humanId} 열기
                            </Button>
                          ))
                        ) : (
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setSoCreateOpen(true)}
                            disabled={update.isPending}
                          >
                            서비스 오더로 전환
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      <ConfirmActionModal
        open={resolveOpen}
        onClose={() => {
          setResolveOpen(false);
          setResolveNote('');
        }}
        title="해결 처리"
        context={
          selectedQ.data
            ? `${selectedQ.data.displayId ?? '–'} · ${CATEGORY_LABEL[selectedQ.data.category]} · ${selectedQ.data.robot.serialShort ?? selectedQ.data.robot.serialLong ?? '–'}`
            : undefined
        }
        submitLabel="해결"
        submitDisabled={!resolveNote.trim()}
        isSubmitting={update.isPending}
        onSubmit={() => {
          if (!selectedQ.data || !resolveNote.trim()) return;
          update.mutate({
            id: selectedQ.data.id,
            body: { status: ReportStatus.RESOLVED, resolution: resolveNote.trim() },
            doneMessage: '리포트를 해결 처리했습니다.',
          });
        }}
      >
        <Field label="해결 메모" required>
          <Textarea
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
            placeholder="해결 내용을 적어 주세요"
            rows={4}
            required
          />
        </Field>
      </ConfirmActionModal>

      {selectedQ.data && (
        <ServiceOrderCreateModal
          open={soCreateOpen}
          onClose={() => setSoCreateOpen(false)}
          defaultRobotId={selectedQ.data.robot.id}
          defaultDescription={selectedQ.data.description}
          onCreated={(soId) => {
            // 리포트 → SO 승격. 리포트는 IN_PROGRESS 로 마킹해서 "이미
            // 처리 중" 임을 시각적으로 표현. 같은 리포트로 SO 가 한 번
            // 더 만들어지는 것을 막진 않지만 운영자가 인지 가능.
            if (selectedQ.data) {
              update.mutate({
                id: selectedQ.data.id,
                body: { status: ReportStatus.IN_PROGRESS },
                doneMessage: '리포트를 진행중으로 바꿨습니다.',
              });
            }
            toast.success(
              '서비스 오더를 만들고 리포트를 진행중으로 바꿨습니다.',
              '서비스 오더 전환',
            );
            // 새 SO 의 인박스 행으로 점프 (PartLotImpactPage 의 state.openId
            // 와 동일 패턴 — Inbox 가 이를 읽으면 자동 선택).
            navigate('/service-orders', { state: { openId: soId } });
          }}
        />
      )}

      <ConfirmActionModal
        open={dismissOpen}
        onClose={() => {
          setDismissOpen(false);
          setDismissReason('');
        }}
        title="반려 처리"
        context={
          selectedQ.data
            ? `${selectedQ.data.displayId ?? '–'} · ${CATEGORY_LABEL[selectedQ.data.category]} · ${selectedQ.data.robot.serialShort ?? selectedQ.data.robot.serialLong ?? '–'}`
            : undefined
        }
        submitLabel="반려"
        submitVariant="danger"
        submitDisabled={!dismissReason.trim()}
        isSubmitting={update.isPending}
        onSubmit={() => {
          if (!selectedQ.data || !dismissReason.trim()) return;
          update.mutate({
            id: selectedQ.data.id,
            body: { status: ReportStatus.DISMISSED, dismissReason: dismissReason.trim() },
            doneMessage: '리포트를 반려했습니다.',
          });
        }}
      >
        <Field label="반려 사유" required>
          <Textarea
            value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
            placeholder="반려 사유를 적어 주세요"
            rows={4}
            required
          />
        </Field>
      </ConfirmActionModal>

      {/* data-lifecycle §3/D3 (PR-5b) — 완전삭제 2단계 확인(되돌릴 수 없음). */}
      <ConfirmActionModal
        open={!!hardDeleteId}
        onClose={() => setHardDeleteId(null)}
        title="리포트 완전 삭제"
        context={
          hardDeleteId
            ? (items.find((r) => r.id === hardDeleteId)?.displayId ?? undefined)
            : undefined
        }
        submitLabel="완전 삭제"
        submitVariant="danger"
        isSubmitting={hardDelete.isPending}
        onSubmit={() => {
          if (hardDeleteId) hardDelete.mutate(hardDeleteId);
        }}
      >
        <p style={{ fontSize: 13, lineHeight: 1.6 }}>
          이 리포트를 영구적으로 삭제합니다. <b>되돌릴 수 없습니다.</b>
          <br />이 리포트로 만든 현장 방문이나 서비스 오더가 있으면 삭제되지 않습니다. 중앙 감사
          로그는 남습니다.
        </p>
      </ConfirmActionModal>
    </>
  );
}

function lifecycleBtn(color: string): CSSProperties {
  return {
    fontSize: 11,
    padding: '2px 8px',
    border: `1px solid ${color}`,
    background: 'var(--bg-surface)',
    color,
    borderRadius: 'var(--r-btn)',
    cursor: 'pointer',
    fontWeight: 600,
  };
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 13 }}>
      <div style={{ width: 72, color: 'var(--fg-muted)' }}>{k}</div>
      <div>{v}</div>
    </div>
  );
}

const tableStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};
const td: CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
const empty: CSSProperties = {
  height: '100%',
  minHeight: 320,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--fg-muted)',
  padding: 32,
};
const select: CSSProperties = {
  height: 32,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  fontSize: 13,
};
